import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowUpDown, Loader2, Pencil, RotateCcw, ScrollText, Trash2 } from 'lucide-react';
import { api } from '../../api/client';
import {
  CERT_ADJUSTMENT_LABELS,
  certAdjustmentLabel,
  invalidateCertViews,
  type CertAdjustmentRecord,
  type CertReconciliation,
} from './CertAdjustments';
import { calendarDayString, parseInstantDate, zonedParts } from '../../lib/calendar-date';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { Tooltip } from '../ui/tooltip';

/**
 * The certification-day LEDGER for one unit.
 *
 * ── WHY THIS EXISTS ─────────────────────────────────────────────────────────
 *
 * `GET /equipment/:id/cert-adjustments` has returned the full ledger — kind,
 * note, actor, entered-at, counter before and after, and which correction
 * superseded which row — since migration 0356. Nothing rendered it. The one
 * consumer built a lookup map out of it to decide where to put an Edit pencil
 * and threw the rest away.
 *
 * So an office that had entered 396 rows off a paper log could see the DAYS, in
 * the Usage Log, and could not see: the note they typed, who entered it, when
 * they entered it, what each row did to the counter, which kind of entry it
 * was, or which rows had already been corrected. Every hand-entered row
 * rendered as the same two words — "Manual entry" — because that is the one
 * label a customer-facing document should carry, and the office surface had
 * been given the customer's vocabulary.
 *
 * ── THIS IS THE OFFICE SURFACE. THE USAGE LOG IS THE CERTIFICATE SURFACE ────
 *
 * They answer different questions and are deliberately not merged:
 *
 *   Usage Log  what counts against THIS certificate. Ticket days and hand
 *              entries together, scoped to the current cycle, in the same
 *              vocabulary the customer PDF prints.
 *   Ledger     what has been asserted about this counter, ever, by whom, and
 *              what each assertion did to it. Every cycle. Office vocabulary.
 *
 * ── CYCLE SCOPE: WIDENED, AND SAID SO ───────────────────────────────────────
 *
 * The Usage Log's adjustment half joins on `cert_start_date_at_entry`, so
 * moving a unit's cert start date makes every hand-entered row vanish from it —
 * along with the Edit pencils that live on those rows, which are the only way
 * to correct an entry. That is a real trap and it needed one of two answers:
 * widen this screen, or say plainly that it is scoped.
 *
 * WIDENED. `listCertAdjustments` was never cycle-scoped, so this is the API's
 * existing behaviour rendered rather than a change to it — and it means a cycle
 * change can no longer hide an entry. The cycle each row was entered against is
 * a COLUMN here, so a reader can still tell which certificate it belonged to,
 * and the note under the heading says which of the two tables they are reading.
 */

interface Props {
  equipmentId: string;
  adjustments: CertAdjustmentRecord[];
  reconciliation: CertReconciliation | null;
  /** The unit's current cert start date, to mark which rows are this cycle's. */
  certStartDate: string | null;
  /** Offered per row when the entry may still be corrected. */
  onCorrect?: (entry: CertAdjustmentRecord) => void;
  canCorrect: boolean;
  /**
   * A request from ELSEWHERE ON THE PAGE to bring rows into view and tick them.
   *
   * The Usage Log above renders the same hand-entered days and can only correct
   * them, which is where TNT got stuck: 67 entries on the wrong unit, every one
   * showing a pencil, and the ledger that can remove them below 71 rows of
   * table. So the Usage Log hands the selection down rather than describing
   * where to go.
   *
   * `nonce` is what makes a REPEAT of the same request fire. Asking twice for
   * the same ids is the ordinary case — click the badge, scroll away, click it
   * again — and an effect keyed on the ids alone goes inert the second time.
   */
  focus?: { ids: string[]; nonce: number } | null;
}

type SortKey = 'enteredAt' | 'workDate' | 'days';
type KindFilter = 'all' | CertAdjustmentRecord['kind'];

/** A calendar day, rendered without constructing a Date. */
function day(value: string | null): string {
  if (!value) return '—';
  return calendarDayString(value) ?? String(value);
}

/**
 * A timestamp, as a day plus the clock time the office entered it at — on the
 * TENANT's clock (`companies.time_zone`), not the viewer's device. No zone, no
 * stamp: an em dash rather than a guessed day.
 */
function stamp(value: string | null, timeZone: string | null): string {
  if (!value) return '—';
  const d = parseInstantDate(value);
  if (!d) return calendarDayString(value) ?? String(value);
  const p = zonedParts(d, timeZone);
  if (!p) return '—';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${p.year}-${pad(p.month)}-${pad(p.day)} ${pad(p.hour)}:${pad(p.minute)}`;
}

/** A signed day count. `+1` and `−20` both say which direction the counter went. */
function signedDays(n: number): string {
  if (n > 0) return `+${n}`;
  // U+2212, not a hyphen: it aligns with the digits in a tabular-nums column.
  if (n < 0) return `−${Math.abs(n)}`;
  return '0';
}

/**
 * How the counter reconciles, in a sentence rather than a number alone.
 *
 * A bare "−11" tells a reader nothing they can act on. Each branch says what
 * the number means, what caused it, and — the part that matters for the
 * negative case — whether anything is going to warn them about it again.
 */
const ReconciliationStrip: React.FC<{ r: CertReconciliation | null }> = ({ r }) => {
  // Absent is not zero. Before the API carrying this is deployed, and on a
  // stale cached response, there is no reading — and "0 unexplained" is the one
  // sentence that must not be shown when nothing was measured.
  if (!r) return null;

  const { counter, explained, residual } = r;
  const tone =
    residual === 0 ? 'border-fieldiq-border'
      : residual > 0 ? 'border-amber-500/40'
        : 'border-red-500/40';

  return (
    <div className={`bg-fieldiq-dark rounded-lg border ${tone} p-3 mb-3`} data-testid="cert-reconciliation">
      <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1 text-sm">
        <span className="text-fieldiq-muted">
          Counter{' '}
          {/* `=== null`, not a falsy check. A counter of 0 is an answer —
              "counted, nought used" — and reading it as absent here would tell
              the operator the opposite of the truth. */}
          <span className="text-white font-bold tabular-nums">
            {counter === null ? '—' : counter}
          </span>
        </span>
        <span className="text-fieldiq-muted">
          Explained by entries{' '}
          <span className="text-white font-bold tabular-nums">{explained}</span>
        </span>
        <span className="text-fieldiq-muted">
          Difference{' '}
          <span className={`font-bold tabular-nums ${
            residual === 0 ? 'text-white' : residual > 0 ? 'text-amber-400' : 'text-red-400'
          }`}>
            {signedDays(residual)}
          </span>
        </span>
      </div>

      {residual === 0 && (
        <p className="text-xs text-fieldiq-muted mt-2">
          The counter matches the entries below.
        </p>
      )}

      {residual > 0 && (
        <p className="text-xs text-amber-300/90 mt-2">
          {residual} day{residual === 1 ? '' : 's'} on the counter that no entry below explains —
          typed straight onto the counter before this ledger existed. Adding a historical day is
          refused unless you confirm, because the new days would stack on top of these and count
          them twice. Recording an opening balance stating the true total resolves it.
        </p>
      )}

      {residual < 0 && (
        <p className="text-xs text-red-300/90 mt-2">
          The entries below add up to {explained} day{explained === 1 ? '' : 's'}, but the counter
          reads {counter === null ? '—' : counter} — {Math.abs(residual)} fewer. The counter was
          changed without an entry recording it, which was possible before this ledger covered that
          edit. <span className="font-semibold">Nothing warns about this and no save is refused
          because of it</span>, so it is shown here rather than left to be discovered.
        </p>
      )}

      {r.anchorId && r.anchorKind && (
        <p className="text-xs text-fieldiq-muted mt-2">
          Measured from the {CERT_ADJUSTMENT_LABELS[r.anchorKind].toLowerCase()} of{' '}
          {day(r.anchorAt)}, which states what the count is; entries before it are not added on top.
        </p>
      )}
    </div>
  );
};

/**
 * The gap that separates one SITTING from the next.
 *
 * A batch is entered in one go, and the natural unit of a mistake is that go —
 * "everything I typed for this unit on Monday", not "these 43 rows I picked out
 * by hand". Measured against TNT's 396 entries on 2026-09-09: they were worked
 * one unit to completion then the next, with ZERO interleaving (9 unit switches
 * across 10 units) and a largest within-unit gap of 9m26s. Thirty minutes sits
 * well clear of that and well inside the gaps between units.
 *
 * For that data every unit holds exactly ONE sitting, so `Select the sitting`
 * and `Select all` coincide there — which is the honest thing to say about it.
 * The clustering earns its place on the unit where somebody comes back a week
 * later and adds four more rows, and wants the four rather than all 107.
 */
const SITTING_GAP_MS = 30 * 60 * 1000;

// The same two class strings the entry panel uses, restated rather than
// exported across files: they are presentation, not a contract, and a shared
// constant would make a styling tweak in one form silently move the other.
const inputClass =
  'w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white text-sm placeholder-fieldiq-muted focus:outline-none focus:border-amber-500/50 transition-colors';
const labelClass = 'block text-xs text-fieldiq-text mb-1.5 font-medium';

export const CertLedger: React.FC<Props> = ({
  equipmentId, adjustments, reconciliation, certStartDate, onCorrect, canCorrect, focus,
}) => {
  const qc = useQueryClient();
  const timeZone = useTenantTimeZone();
  const [sortKey, setSortKey] = useState<SortKey>('enteredAt');
  const [asc, setAsc] = useState(false);
  const [kind, setKind] = useState<KindFilter>('all');
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState('');

  const cardRef = useRef<HTMLDivElement | null>(null);

  /**
   * Take a focus request from the page: clear any filter that would hide the
   * rows, tick them, and bring the card into view.
   *
   * THE FILTERS ARE RESET DELIBERATELY. A request naming ids the current kind
   * chip or search box excludes would tick rows the operator cannot see, and
   * the selection bar would then offer to remove entries that are not on
   * screen. Clearing both is the only way the count in that bar and the ticked
   * rows are the same set.
   *
   * Only LIVE ids are taken. An already-removed entry cannot be removed again,
   * and including it would inflate the "−N days off the count" figure the bar
   * shows before the reason is typed.
   */
  useEffect(() => {
    if (!focus || focus.ids.length === 0) return;
    const live = new Set(adjustments.filter((a) => !a.deletedAt).map((a) => a.id));
    const take = focus.ids.filter((id) => live.has(id));
    if (take.length === 0) return;
    setKind('all');
    setQ('');
    setSelected(new Set(take));
    // Guarded, and the order matters: the SELECTION is the load-bearing half
    // and is already set above. Scrolling is a courtesy, so a host that does
    // not implement it (jsdom, and any embedded view) must not take the
    // removal path down with it.
    cardRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    // `adjustments` is deliberately NOT a dependency: this fires on a REQUEST,
    // not on every refetch. Including it would re-tick the same rows each time
    // the ledger reloads, undoing an operator's own untick after a removal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus?.nonce]);

  /** Which corrections point at which rows, so a row can name its corrector. */
  const byId = useMemo(
    () => new Map(adjustments.map((a) => [a.id, a])),
    [adjustments]
  );

  /**
   * Only the kinds actually PRESENT get a filter chip.
   *
   * A fixed set of five would offer "Cycle reset" on a unit that has never had
   * one, and clicking it renders an empty table that reads as a broken filter.
   */
  const kindsPresent = useMemo(() => {
    const seen = new Set<CertAdjustmentRecord['kind']>();
    for (const a of adjustments) seen.add(a.kind);
    return (Object.keys(CERT_ADJUSTMENT_LABELS) as CertAdjustmentRecord['kind'][])
      .filter((k) => seen.has(k));
  }, [adjustments]);

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const filtered = adjustments.filter((a) => {
      if (kind !== 'all' && a.kind !== kind) return false;
      if (!needle) return true;
      // Everything a person might remember typing. The note is the field this
      // screen exists to surface, so it is searchable rather than only visible.
      return [a.note, a.rigName, a.customerName, a.actorEmail, a.workDate]
        .some((v) => (v ? String(v).toLowerCase().includes(needle) : false));
    });

    const dir = asc ? 1 : -1;
    return [...filtered].sort((a, b) => {
      if (sortKey === 'days') return (a.days - b.days) * dir;
      // NULLS LAST in both directions: an opening balance may carry no as-of
      // date, and sorting it to the top of a date-ordered list puts an undated
      // row above every dated one whichever way the arrow points.
      const av = sortKey === 'workDate' ? a.workDate : a.createdAt;
      const bv = sortKey === 'workDate' ? b.workDate : b.createdAt;
      if (!av && !bv) return 0;
      if (!av) return 1;
      if (!bv) return -1;
      return av < bv ? -dir : av > bv ? dir : 0;
    });
  }, [adjustments, kind, q, sortKey, asc]);

  /** Entries still counting. Only these can be selected for removal. */
  const liveRows = useMemo(() => rows.filter((r) => !r.deletedAt), [rows]);

  /**
   * The sitting containing a given entry: the maximal run of LIVE entries whose
   * consecutive entered-at gaps are all under SITTING_GAP_MS.
   *
   * Computed over every live entry in entered order, NOT over the filtered
   * view. A sitting is a fact about when somebody typed, and a search box
   * cannot change it — expanding only to the rows that happen to match the
   * current filter would hand back a selection that is neither the sitting nor
   * what the operator sees.
   */
  const sittingOf = useMemo(() => {
    const live = adjustments.filter((a) => !a.deletedAt)
      .slice().sort((a, b) => (a.createdAt < b.createdAt ? -1 : a.createdAt > b.createdAt ? 1 : 0));
    const groups: string[][] = [];
    let current: string[] = [];
    let prev: number | null = null;
    for (const a of live) {
      const t = new Date(a.createdAt).getTime();
      if (prev !== null && Number.isFinite(t) && t - prev > SITTING_GAP_MS) {
        groups.push(current); current = [];
      }
      current.push(a.id);
      if (Number.isFinite(t)) prev = t;
    }
    if (current.length) groups.push(current);
    const map = new Map<string, string[]>();
    for (const g of groups) for (const id of g) map.set(id, g);
    return map;
  }, [adjustments]);

  const allVisibleSelected = liveRows.length > 0 && liveRows.every((r) => selected.has(r.id));

  /**
   * What the selection would take off the counter.
   *
   * Summed across EVERY adjustment, not the filtered view: a selection can
   * survive a filter change, and showing the total of only the rows currently
   * on screen would understate what the button is about to do.
   */
  const selectedDays = useMemo(
    () => adjustments.filter((a) => selected.has(a.id)).reduce((n, a) => n + a.days, 0),
    [adjustments, selected]
  );

  const toggleOne = (id: string) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const toggleAllVisible = () => setSelected((prev) => {
    if (liveRows.length > 0 && liveRows.every((r) => prev.has(r.id))) {
      const next = new Set(prev);
      for (const r of liveRows) next.delete(r.id);
      return next;
    }
    const next = new Set(prev);
    for (const r of liveRows) next.add(r.id);
    return next;
  });

  const selectSitting = (id: string) => setSelected((prev) => {
    const next = new Set(prev);
    for (const sid of sittingOf.get(id) ?? [id]) next.add(sid);
    return next;
  });

  const clearSelection = () => { setSelected(new Set()); setConfirming(false); setReason(''); };

  const remove = useMutation({
    mutationFn: () => api.equipment.deleteCertAdjustments(equipmentId, {
      ids: Array.from(selected), reason: reason.trim(),
    }),
    onSuccess: (res: any) => {
      const n = res?.data?.data?.ids?.length ?? selected.size;
      invalidateCertViews(qc, equipmentId);
      clearSelection();
      toast.success(`${n} entr${n === 1 ? 'y' : 'ies'} removed from the count`);
    },
    // The server's own sentence, never a generic one. Both refusals it can
    // give here name numbers the person needs — how far below zero, and by
    // how much the counter and the entries already disagree.
    onError: (err: any) => toast.error(
      err?.response?.data?.error || 'Could not remove those entries.'),
  });

  const putBack = useMutation({
    mutationFn: (id: string) => api.equipment.restoreCertAdjustments(equipmentId, { ids: [id] }),
    onSuccess: () => {
      invalidateCertViews(qc, equipmentId);
      toast.success('Entry restored');
    },
    onError: (err: any) => toast.error(
      err?.response?.data?.error || 'Could not restore that entry.'),
  });

  const toggleSort = (k: SortKey) => {
    if (k === sortKey) { setAsc((v) => !v); return; }
    setSortKey(k); setAsc(false);
  };

  const SortHeader: React.FC<{ k: SortKey; children: React.ReactNode }> = ({ k, children }) => (
    <th className="text-left px-3 py-2.5 font-medium">
      <button
        type="button" onClick={() => toggleSort(k)}
        className={`inline-flex items-center gap-1 hover:text-white transition-colors ${
          sortKey === k ? 'text-white' : ''
        }`}
        aria-label={`Sort by ${String(children)}`}
      >
        {children}
        <ArrowUpDown size={11} className={sortKey === k ? 'opacity-100' : 'opacity-40'} />
      </button>
    </th>
  );

  return (
    <div
      ref={cardRef}
      data-testid="cert-ledger-card"
      className="bg-fieldiq-card rounded-lg border border-fieldiq-border overflow-hidden scroll-mt-4"
    >
      <div className="p-4 border-b border-fieldiq-border">
        <div className="flex items-center gap-2">
          <ScrollText size={16} className="text-fieldiq-accent" />
          <h3 className="text-base font-bold text-white">Certification Day Ledger</h3>
        </div>
        <p className="text-xs text-fieldiq-text mt-1">
          {/* The COUNT this table actually holds. The Usage Log's own header
              counts ticket rows only, which reads "0 records" above a table of
              107 hand-entered ones. */}
          {adjustments.length} entr{adjustments.length === 1 ? 'y' : 'ies'}
          {rows.length !== adjustments.length ? ` · ${rows.length} shown` : ''}
          {' · '}
          every certification cycle, not only the current one. The Usage Log above is scoped to this
          certificate.
        </p>
      </div>

      <div className="p-4">
        <ReconciliationStrip r={reconciliation} />

        {adjustments.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <button
              type="button" onClick={() => setKind('all')}
              className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors ${
                kind === 'all'
                  ? 'bg-fieldiq-accent text-fieldiq-dark border-fieldiq-accent'
                  : 'bg-fieldiq-dark text-fieldiq-text border-fieldiq-border hover:border-fieldiq-accent'
              }`}
            >
              All
            </button>
            {kindsPresent.map((k) => (
              <button
                key={k} type="button" onClick={() => setKind(k)}
                className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors ${
                  kind === k
                    ? 'bg-fieldiq-accent text-fieldiq-dark border-fieldiq-accent'
                    : 'bg-fieldiq-dark text-fieldiq-text border-fieldiq-border hover:border-fieldiq-accent'
                }`}
              >
                {CERT_ADJUSTMENT_LABELS[k]}
              </button>
            ))}
            <input
              className="flex-1 min-w-[12rem] bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-1.5 text-white text-xs placeholder-fieldiq-muted focus:outline-none focus:border-amber-500/50"
              placeholder="Search note, rig, oil company, who entered it…"
              aria-label="Search the certification day ledger"
              value={q} onChange={(e) => setQ(e.target.value)}
            />
          </div>
        )}

        {selected.size > 0 && (
          <div className="bg-fieldiq-dark rounded-lg border border-amber-500/40 p-3 mb-3" data-testid="cert-selection-bar">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-white">
                <span className="font-bold tabular-nums">{selected.size}</span>{' '}
                entr{selected.size === 1 ? 'y' : 'ies'} selected
                {/* The number that decides whether this is safe. An operator
                    removing a batch is moving a compliance figure, and they
                    should see by how much BEFORE they type a reason. */}
                <span className="text-fieldiq-muted">
                  {' · '}{signedDays(-selectedDays)} day{Math.abs(selectedDays) === 1 ? '' : 's'} off the count
                </span>
              </p>
              <div className="flex items-center gap-2">
                <button type="button" onClick={clearSelection}
                  className="text-xs text-fieldiq-muted hover:text-white transition-colors">
                  Clear
                </button>
                {!confirming && (
                  <button
                    type="button" onClick={() => setConfirming(true)}
                    className="inline-flex items-center gap-1.5 bg-red-500/15 border border-red-500/40 text-red-300 px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-red-500/25 transition-colors"
                  >
                    <Trash2 size={13} /> Remove from the count
                  </button>
                )}
              </div>
            </div>

            {confirming && (
              <div className="mt-3 pt-3 border-t border-fieldiq-border">
                {/* ONE reason for the whole selection. The API takes it per
                    request rather than per row for exactly this: 107 rows
                    carrying the same sentence is not a record anybody reads. */}
                <label className={labelClass} htmlFor="cert-delete-reason">
                  Why are these being removed? <span className="text-amber-400">Required</span>
                </label>
                <input
                  id="cert-delete-reason" className={inputClass}
                  placeholder="e.g. Entered against TN 1101 by mistake — belongs on TN 1102"
                  value={reason} onChange={(e) => setReason(e.target.value)}
                />
                <p className="text-xs text-fieldiq-muted mt-1.5">
                  They stay on this ledger, struck through, with your name and this reason — so the
                  record still answers what happened. They leave the count, the usage log and the
                  certification PDF. You can put them back.
                </p>
                <div className="flex items-center gap-2 mt-3">
                  <button
                    type="button" disabled={reason.trim() === '' || remove.isPending}
                    onClick={() => remove.mutate()}
                    className="bg-red-500/20 border border-red-500/50 text-red-200 px-4 py-2 rounded-lg text-xs font-semibold hover:bg-red-500/30 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {remove.isPending
                      ? <Loader2 size={13} className="animate-spin inline" />
                      : `Remove ${selected.size} entr${selected.size === 1 ? 'y' : 'ies'}`}
                  </button>
                  <button type="button" onClick={() => { setConfirming(false); setReason(''); }}
                    className="bg-fieldiq-card border border-fieldiq-border text-white px-4 py-2 rounded-lg text-xs font-medium hover:border-fieldiq-muted transition-colors">
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {adjustments.length === 0 ? (
          <div className="py-8 text-center text-fieldiq-muted text-sm">
            No certification days have been entered by hand on this unit.
            <p className="text-xs mt-2">
              Days counted from approved tickets are not entries — they appear in the Usage Log.
            </p>
          </div>
        ) : rows.length === 0 ? (
          <div className="py-8 text-center text-fieldiq-muted text-sm">
            No entries match this filter.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="cert-ledger-table">
              <thead>
                <tr className="bg-fieldiq-dark text-fieldiq-muted text-xs uppercase">
                  {canCorrect && (
                    <th className="px-3 py-2.5 w-8">
                      {/* Selects every LIVE row currently visible, so it
                          composes with the filter and the search: filter to
                          Historical day, search a rig, tick this, and the
                          selection is exactly what is on screen. A removed row
                          is not selectable — it is already out of the count. */}
                      <input
                        type="checkbox"
                        className="accent-amber-500"
                        aria-label="Select every entry shown"
                        checked={allVisibleSelected}
                        onChange={toggleAllVisible}
                        disabled={liveRows.length === 0}
                      />
                    </th>
                  )}
                  <SortHeader k="enteredAt">Entered</SortHeader>
                  <th className="text-left px-3 py-2.5 font-medium">Entry</th>
                  <SortHeader k="workDate">Date</SortHeader>
                  <SortHeader k="days">Days</SortHeader>
                  <th className="text-left px-3 py-2.5 font-medium">Counter</th>
                  <th className="text-left px-3 py-2.5 font-medium">Rig / Oil company</th>
                  <th className="text-left px-3 py-2.5 font-medium">Note</th>
                  <th className="text-left px-3 py-2.5 font-medium">By</th>
                  <th className="text-left px-3 py-2.5 font-medium">Status</th>
                  <th className="text-right px-3 py-2.5 font-medium"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => {
                  const corrector = a.supersededById ? byId.get(a.supersededById) : undefined;
                  const corrects = a.supersedesId ? byId.get(a.supersedesId) : undefined;
                  // The API's own rule: one correction per entry, and only an
                  // opening balance or a historical day may be corrected.
                  // Offering the pencil anywhere else buys a 409 nobody can act
                  // on.
                  const correctable =
                    canCorrect && !a.supersededById && !a.deletedAt
                    && (a.kind === 'opening_balance' || a.kind === 'historical_day');
                  // Which certificate this entry was made against. Rows from a
                  // previous cycle are still shown — that is the point of this
                  // table — but a reader has to be able to tell.
                  const otherCycle =
                    !!certStartDate && !!a.certStartDateAtEntry
                    && day(a.certStartDateAtEntry) !== day(certStartDate);

                  return (
                    <tr
                      key={a.id}
                      data-testid={a.deletedAt ? 'cert-ledger-row-removed' : 'cert-ledger-row'}
                      className={`border-t border-fieldiq-border hover:bg-fieldiq-dark/50 transition-colors ${
                        a.deletedAt ? 'opacity-50' : a.supersededById ? 'opacity-60' : ''
                      }`}
                    >
                      {canCorrect && (
                        <td className="px-3 py-2.5">
                          {/* A removed entry has no checkbox at all rather than
                              a disabled one: it is not a thing that can be
                              removed, and a greyed box invites the click. */}
                          {!a.deletedAt && (
                            <input
                              type="checkbox"
                              className="accent-amber-500"
                              aria-label={`Select the ${certAdjustmentLabel(a.kind).toLowerCase()} of ${day(a.workDate)}`}
                              checked={selected.has(a.id)}
                              onChange={() => toggleOne(a.id)}
                            />
                          )}
                        </td>
                      )}
                      <td className="px-3 py-2.5 text-fieldiq-text whitespace-nowrap tabular-nums">
                        {stamp(a.createdAt, timeZone)}
                        {canCorrect && !a.deletedAt && (sittingOf.get(a.id)?.length ?? 0) > 1 && (
                          <Tooltip content="Select every entry typed in the same sitting as this one" className="w-full">
                            <button
                              type="button" onClick={() => selectSitting(a.id)}
                              className="block text-[11px] text-fieldiq-muted hover:text-fieldiq-accent transition-colors"
                            >
                              + sitting ({sittingOf.get(a.id)!.length})
                            </button>
                          </Tooltip>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-white whitespace-nowrap">
                        {certAdjustmentLabel(a.kind)}
                        {a.source === 'import' && (
                          <span className="text-fieldiq-muted text-xs"> · imported</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-fieldiq-text whitespace-nowrap">
                        {day(a.workDate)}
                        {otherCycle && (
                          <span
                            className="text-fieldiq-muted text-xs block"
                            title={`Entered against the certification cycle beginning ${day(a.certStartDateAtEntry)}`}
                          >
                            cycle {day(a.certStartDateAtEntry)}
                          </span>
                        )}
                      </td>
                      <td className={`px-3 py-2.5 whitespace-nowrap tabular-nums font-medium ${
                        a.days < 0 ? 'text-red-400' : 'text-white'
                      }`}>
                        {signedDays(a.days)}
                      </td>
                      <td className="px-3 py-2.5 text-fieldiq-text whitespace-nowrap tabular-nums">
                        {/* `=== null` is NEVER COUNTED, and it is the state an
                            opening balance exists to leave. Rendering it as 0
                            erases which of the two the unit was in. */}
                        {a.counterBefore === null ? '—' : a.counterBefore} → {a.counterAfter}
                      </td>
                      <td className="px-3 py-2.5 text-fieldiq-text">
                        {a.rigName || a.customerName
                          ? [a.rigName, a.customerName].filter(Boolean).join(' · ')
                          : '—'}
                      </td>
                      <td className="px-3 py-2.5 text-fieldiq-text max-w-[18rem]">
                        {a.note ? <span title={a.note}>{a.note}</span> : '—'}
                      </td>
                      <td className="px-3 py-2.5 text-fieldiq-text whitespace-nowrap">
                        {a.actorEmail || '—'}
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap text-xs">
                        {a.deletedAt ? (
                          // REMOVED wins over corrected. An entry can be both,
                          // and the fact that matters to a reader of a
                          // certification record is that it is no longer
                          // counted — a row reading "Corrected −2" while
                          // contributing nothing is the confident wrong answer
                          // this whole surface is built against.
                          <span
                            className="text-red-300"
                            title={a.deleteReason ?? undefined}
                          >
                            Removed{a.deletedByEmail ? ` by ${a.deletedByEmail}` : ''}
                          </span>
                        ) : corrector ? (
                          <span className="text-amber-300" title={`Corrected on ${stamp(corrector.createdAt, timeZone)}`}>
                            Corrected {signedDays(corrector.days)}
                          </span>
                        ) : corrects ? (
                          <span className="text-fieldiq-muted">
                            Corrects the {certAdjustmentLabel(corrects.kind).toLowerCase()} of {day(corrects.workDate)}
                          </span>
                        ) : (
                          <span className="text-fieldiq-muted">Stands</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap text-right">
                        {a.deletedAt && canCorrect && (
                          <Tooltip content="Put this entry back into the count">
                            <button
                              type="button" onClick={() => putBack.mutate(a.id)}
                              disabled={putBack.isPending}
                              className="text-fieldiq-muted hover:text-fieldiq-accent transition-colors disabled:opacity-40"
                              aria-label={`Restore the ${certAdjustmentLabel(a.kind).toLowerCase()} of ${day(a.workDate)}`}
                            >
                              <RotateCcw size={14} />
                            </button>
                          </Tooltip>
                        )}
                        {correctable && onCorrect && (
                          <Tooltip content="Correct this entry — records a new correcting row, the original is kept">
                            <button
                              type="button" onClick={() => onCorrect(a)}
                              className="text-fieldiq-muted hover:text-fieldiq-accent transition-colors"
                              aria-label={`Correct the ${certAdjustmentLabel(a.kind).toLowerCase()} of ${day(a.workDate)}`}
                            >
                              <Pencil size={14} />
                            </button>
                          </Tooltip>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <p className="text-xs text-fieldiq-muted mt-3">
          Entries are never edited. Correcting one records a new entry carrying the difference and
          the reason, and the original stays exactly as it was written. Removing one takes its days
          off the count and off the certification PDF, and keeps the entry here — struck through,
          with who removed it and why — so the record still answers what happened. Removed entries
          can be put back.
        </p>
      </div>
    </div>
  );
};
